import { createAction } from '@reduxjs/toolkit';
export const clearLastCreateAttemptResult = createAction('atgPayAuction/clearLastCreateAttemptResult');
import { AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    ATGPayAuction,
    ATGPayAuctionSuccessPayload,
    ATGPayInvoicePayload,
    RecalculateInvoicesPayload,
} from '@/redux/modules/atgPay/atgPayAuction/atgPayAuction.types';
import {
    getATGPayAuction,
    getATGPayInvoice,
    getOrGenerateJWT,
    recalculateInvoices,
} from '@/redux/modules/atgPay/atgPayAuction/atgPayAuction.api';
import { getDeployment } from '@/redux/modules/config';
import { getUserId } from '@/redux/modules/user';

export const fetchATGPayAuction = createAsyncThunk<
    ATGPayAuctionSuccessPayload,
    ATGPayAuction,
    AsyncThunkConfigWithRejectValue
>('la/ui/atgPayAuction/fetchATGPayAuction', async ({ catalogId, marketplaceCode }, { getState, rejectWithValue }) => {
    const state = getState();
    const deployment = getDeployment(state);
    const supportId = getUserId(state);

    if (!supportId) {
        return rejectWithValue('User ID not available' as any);
    }

    try {
        const { payload } = await getATGPayAuction({
            catalogId,
            deployment,
            marketplaceCode,
            supportId,
        });

        if (typeof payload === 'string') {
            throw payload;
        }
        // Add catalogId to the payload since the API may not return it
        return {
            ...payload,
            catalogId,
        };
    } catch (error) {
        // Handle various error formats
        if (typeof error === 'string') {
            return rejectWithValue(error as any);
        }
        if (error && typeof error === 'object') {
            const errorObj = error as any;
            const message = errorObj.message || errorObj.errorMessage || errorObj.error || JSON.stringify(error);
            return rejectWithValue(message as any);
        }
        return rejectWithValue('An unknown error occurred' as any);
    }
});

export const recalculateATGPayInvoices = createAsyncThunk<
    RecalculateInvoicesPayload,
    ATGPayAuction,
    AsyncThunkConfigWithRejectValue
>('la/ui/atgPayAuction/recalculateInvoices', async ({ catalogId, marketplaceCode }, { getState, rejectWithValue }) => {
    const state = getState();
    const deployment = getDeployment(state);
    const supportId = getUserId(state);
    if (!supportId) {
        return rejectWithValue('User ID not available' as any);
    }
    try {
        const { payload } = await recalculateInvoices({
            catalogId,
            deployment,
            marketplaceCode,
            supportId,
        });
        if (typeof payload === 'string') {
            throw payload;
        }
        return payload as RecalculateInvoicesPayload;
    } catch (error) {
        // Handle various error formats
        if (typeof error === 'string') {
            return rejectWithValue(error as any);
        }
        if (error && typeof error === 'object') {
            const errorObj = error as any;
            const message = errorObj.message || errorObj.errorMessage || errorObj.error || JSON.stringify(error);
            return rejectWithValue(message as any);
        }
        return rejectWithValue('An unknown error occurred' as any);
    }
});

export const fetchATGPayJWT = createAsyncThunk<
    { token: string },
    { marketplaceCode: string },
    AsyncThunkConfigWithRejectValue
>('la/ui/atgPayAuction/fetchJWT', async ({ marketplaceCode }, { getState, rejectWithValue }) => {
    const state = getState();
    const deployment = getDeployment(state);
    const supportId = getUserId(state);

    if (!supportId) {
        return rejectWithValue('User ID not available' as any);
    }

    try {
        const response = await getOrGenerateJWT({ deployment, marketplaceCode, supportId });

        if (response.error) {
            throw response.payload;
        }

        return response.payload as { token: string };
    } catch (error) {
        if (typeof error === 'string') {
            return rejectWithValue(error as any);
        }
        if (error && typeof error === 'object') {
            const errorObj = error as any;
            const message = errorObj.message || errorObj.errorMessage || errorObj.error || JSON.stringify(error);
            return rejectWithValue(message as any);
        }
        return rejectWithValue('An unknown error occurred' as any);
    }
});

export const fetchATGPayInvoice = createAsyncThunk<
    ATGPayInvoicePayload,
    { invoiceId: string; marketplaceCode?: string },
    AsyncThunkConfigWithRejectValue
>('la/ui/atgPayAuction/fetchInvoice', async ({ invoiceId, marketplaceCode }, { getState, rejectWithValue }) => {
    const state = getState();
    const deployment = getDeployment(state);
    const supportId = getUserId(state);

    if (!supportId) {
        return rejectWithValue('User ID not available' as any);
    }

    try {
        const response = await getATGPayInvoice({
            deployment,
            invoiceId,
            marketplaceCode,
            supportId,
        });

        if (response.error) {
            throw response.payload;
        }

        return response.payload as ATGPayInvoicePayload;
    } catch (error) {
        if (typeof error === 'string') {
            return rejectWithValue(error as any);
        }
        if (error && typeof error === 'object') {
            const errorObj = error as any;
            const message = errorObj.message || errorObj.errorMessage || errorObj.error || JSON.stringify(error);
            return rejectWithValue(message as any);
        }
        return rejectWithValue('An unknown error occurred' as any);
    }
});
